import { useMemo, type ReactElement } from 'react';
import {
  useWorkoutsInRange,
  type WorkoutsResponse,
} from '@/lib/workouts';
import { useCalendarItems, type CalendarApiItem } from '@/lib/calendar';
import {
  buildMonthGrid,
  dayKeyUTC,
  isSameDayUTC,
  todayUTC,
} from './dateUtils';
import { DayChip } from './DayChip';
import { buildCalendarItemsForDay } from './calendarItems';
import { useShowMealsPref } from './showMealsPref';

/**
 * Vista mensual del calendario `/entrenos` (M2.19).
 *
 * Grid 7×5 o 7×6 (mismo patrón que `MonthCalendar` del dashboard) con
 * primer día Lunes. Cada celda muestra:
 *  - Número del día (top-left).
 *  - Hasta 3 chips con disciplina + min/km.
 *  - "+N más" si hay más.
 *
 * Click en chip → navega a /entreno/$id (manejado por `DayChip`).
 * Click en zona vacía → cambia a vista Día con esa fecha (callback
 * `onSelectDay`).
 */

const MAX_CHIPS_PER_CELL = 3;

export function MonthView({
  cursor,
  onSelectDay,
}: {
  cursor: Date;
  onSelectDay: (d: Date) => void;
}): ReactElement {
  const { fromISO, toISO, cells } = useMemo(
    () => buildMonthGrid(cursor),
    [cursor],
  );
  const query = useWorkoutsInRange(fromISO, toISO);
  const today = todayUTC();
  const monthIndex = cursor.getUTCMonth();
  // M2.48.C — agrega indicador 🍽 en celdas con comidas planificadas.
  const showMeals = useShowMealsPref();
  const calendarItemsQuery = useCalendarItems(fromISO, toISO, showMeals);

  return (
    <section aria-label="Vista mensual del calendario" data-testid="calendar-month-view">
      <WeekdayHeader />

      {query.isError && (
        <p role="alert" className="mt-2 text-xs text-rose-700">
          No se pudieron cargar los entrenos del mes.
        </p>
      )}

      <div
        role="grid"
        aria-label="Días del mes"
        data-testid="calendar-month-grid"
        className={`mt-1 grid grid-cols-7 gap-1 ${query.isLoading ? 'opacity-60' : ''}`}
      >
        {cells.map((d) => (
          <MonthCell
            key={dayKeyUTC(d)}
            date={d}
            inMonth={d.getUTCMonth() === monthIndex}
            isToday={isSameDayUTC(d, today)}
            workouts={query.data ?? null}
            mealsItems={showMeals ? (calendarItemsQuery.data?.items ?? null) : null}
            onSelectDay={onSelectDay}
          />
        ))}
      </div>
    </section>
  );
}

function WeekdayHeader(): ReactElement {
  const headers = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  return (
    <div
      role="row"
      className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-500"
    >
      {headers.map((h) => (
        <span key={h} role="columnheader">
          {h}
        </span>
      ))}
    </div>
  );
}

function MonthCell({
  date,
  inMonth,
  isToday,
  workouts,
  mealsItems,
  onSelectDay,
}: {
  date: Date;
  inMonth: boolean;
  isToday: boolean;
  workouts: WorkoutsResponse | null;
  mealsItems: CalendarApiItem[] | null;
  onSelectDay: (d: Date) => void;
}): ReactElement {
  const dayKey = dayKeyUTC(date);
  // M2.29 — items unificados (planned + actual) con estado para pintar
  // verde=completed, outline=planned, gris=skipped, ámbar=partial, color
  // sólido=actual_unplanned. Dedupe planned↔actual vinculado.
  const items = useMemo(
    () => buildCalendarItemsForDay(workouts, dayKey),
    [dayKey, workouts],
  );
  // M2.48.C — cuántas comidas hay este día (para mostrar 🍽×N).
  const mealCount = useMemo(() => {
    if (!mealsItems) return 0;
    return mealsItems.filter(
      (it) =>
        it.kind === 'meal' && it.datetime.slice(0, 10) === dayKey,
    ).length;
  }, [mealsItems, dayKey]);

  const visible = items.slice(0, MAX_CHIPS_PER_CELL);
  const overflow = Math.max(0, items.length - MAX_CHIPS_PER_CELL);

  const bgClasses = inMonth ? 'bg-white' : 'bg-slate-50';
  const numberClasses = inMonth
    ? isToday
      ? 'font-bold text-teal-700'
      : 'font-medium text-slate-800'
    : 'text-slate-400';
  const ringClasses = isToday ? 'ring-2 ring-teal-500 ring-offset-1' : '';

  function handleCellClick() {
    onSelectDay(date);
  }

  return (
    <div
      role="gridcell"
      data-testid={`calendar-month-cell-${dayKey}`}
      data-off-month={!inMonth ? 'true' : undefined}
      data-today={isToday ? 'true' : undefined}
      className={`relative flex min-h-[88px] flex-col gap-1 rounded-md border border-slate-200 p-1.5 shadow-sm ${bgClasses} ${ringClasses}`}
    >
      <button
        type="button"
        onClick={handleCellClick}
        aria-label={`Ver día ${dayKey}`}
        className="absolute inset-0 cursor-pointer rounded-md focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
      >
        <span className="sr-only">Ver día completo</span>
      </button>

      <div className="relative z-10 flex items-center justify-between">
        <span className={`text-xs ${numberClasses}`}>{date.getUTCDate()}</span>
        {mealCount > 0 && (
          <span
            data-testid={`calendar-month-meals-${dayKey}`}
            title={`${mealCount} comidas planificadas`}
            aria-label={`${mealCount} comidas planificadas`}
            className="rounded bg-amber-100 px-1 text-[9px] font-medium text-amber-900"
          >
            🍽 {mealCount}
          </span>
        )}
      </div>

      <div className="relative z-10 flex flex-col gap-0.5">
        {visible.map((item) => (
          <DayChip key={item.key} item={item} compact />
        ))}
        {overflow > 0 && (
          <span
            data-testid={`calendar-month-overflow-${dayKey}`}
            className="text-[10px] font-medium text-slate-500"
          >
            +{overflow} más
          </span>
        )}
      </div>
    </div>
  );
}
